import { useEffect, useRef } from "react";
import { BarChart3, Compass, Cpu, MessageCircle } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { Overline, Reveal } from "./Reveal";
import { waLink, waSitesLink, WA_MESSAGES, site } from "../config/site";
import { trackEvent } from "../lib/analytics";

const PRINCIPLES = [
  { icon: Compass, title: "Estratégia antes da execução" },
  { icon: BarChart3, title: "Dados para orientar decisões" },
  { icon: Cpu, title: "Tecnologia a serviço do negócio" },
];

const FOUNDERS = [
  {
    photo: "/assets/fundador1.jpg",
    name: "Priscila Gonçalves",
    role: "Fundadora · Tráfego Pago & Estratégias",
    alt: "Priscila Gonçalves, fundadora da GOBAX",
    skills: ["Meta Ads", "Google Ads", "Análise de desempenho"],
    wa: { label: "Falar com a Priscila", message: WA_MESSAGES.trafego, sites: false, id: "priscila" },
  },
  {
    photo: "/assets/fundador2.jpg",
    name: "Lucas Gonçalves",
    role: "Fundador · Criação de Sites",
    alt: "Lucas Gonçalves, fundador da GOBAX",
    skills: ["Sites profissionais", "Landing pages", "Experiência do usuário"],
    wa: { label: "Falar com o Lucas", message: WA_MESSAGES.sites, sites: true, id: "lucas" },
  },
];

export const About = () => {
  const reduce = useReducedMotion();
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    video.load();
    const tryPlay = () => video.play().catch(() => {});
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) tryPlay();
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(video);
    const gestures = ["pointerdown", "touchstart", "scroll"];
    gestures.forEach((g) => window.addEventListener(g, tryPlay, { once: true, passive: true }));
    return () => {
      observer.disconnect();
      gestures.forEach((g) => window.removeEventListener(g, tryPlay));
    };
  }, [reduce]);

  return (
  <section id="sobre" data-testid="about-section" className="relative py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
      <div>
        <Reveal>
          <Overline>Sobre a GOBAX</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
            Mais estratégia. <span className="chrome-text">Menos improviso.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-gb-gray sm:text-lg">
            A GOBAX nasceu com uma proposta clara: ajudar empresas a utilizar a internet de forma
            mais estratégica. Unimos criação de sites, aquisição de tráfego e rastreamento de
            conversões para construir uma presença digital mais organizada, profissional e preparada
            para evoluir.
          </p>
        </Reveal>
        <div className="mt-10 space-y-5">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={0.22 + i * 0.08}>
              <div data-testid={`about-principle-${i + 1}`} className="flex items-center gap-4 rounded-xl border border-white/8 bg-white/[0.02] px-5 py-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gb-silver/25 bg-white/[0.03] text-gb-silver">
                  <p.icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <p className="font-display text-sm font-bold uppercase tracking-[0.12em] text-gb-silverhi">{p.title}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {FOUNDERS.map((f, i) => (
          <Reveal key={f.photo} delay={0.15 + i * 0.12} className={i === 1 ? "mt-10" : ""}>
            <figure data-testid={`about-founder-${i + 1}`} className="card-chrome overflow-hidden rounded-2xl p-2">
              <img
                src={f.photo}
                alt={f.alt}
                width={640}
                height={640}
                loading="lazy"
                className="aspect-square w-full rounded-xl object-cover object-center grayscale transition-all duration-700 hover:grayscale-0"
              />
              <figcaption className="px-3 py-4">
                <p className="font-display text-sm font-bold text-gb-white">{f.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-gb-silver">{f.role}</p>
                <ul className="mt-3 flex flex-wrap gap-1.5">
                  {f.skills.map((s) => (
                    <li key={s} className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em] text-gb-gray">
                      {s}
                    </li>
                  ))}
                </ul>
                <a
                  href={f.wa.sites ? waSitesLink(f.wa.message) : waLink(f.wa.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: `founder_${f.wa.id}` })}
                  data-testid={`founder-wa-${f.wa.id}`}
                  className="btn-ghost-chrome mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-semibold text-gb-white"
                >
                  <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  {f.wa.label}
                </a>
                {f.wa.sites && site.whatsappDisplaySites && (
                  <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-gb-gray/80">{site.whatsappDisplaySites}</p>
                )}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="lg:col-span-2">
        <div className="card-chrome overflow-hidden rounded-2xl p-2">
          {reduce ? (
            <img src="/assets/backstage_poster.jpg" alt="GOBAX em movimento — estratégia, tecnologia e crescimento" width={1280} height={720} loading="lazy" className="aspect-video w-full rounded-xl object-cover" />
          ) : (
            <video
              ref={videoRef}
              poster="/assets/backstage_poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              width={1280}
              height={720}
              data-testid="about-video"
              aria-label="Vídeo institucional GOBAX — Priscila, Lucas e a marca em movimento"
              className="aspect-video w-full rounded-xl object-cover"
            >
              <source src="/assets/backstage.webm" type="video/webm" />
              <source src="/assets/backstage.mp4" type="video/mp4" />
            </video>
          )}
        </div>
        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-gb-gray/70">
          GOBAX em movimento — estratégia, tecnologia e crescimento
        </p>
      </Reveal>
    </div>
  </section>
  );
};
